import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails, styled, Container } from '@mui/material';
import Banner from './Banner';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import dayjs from 'dayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx-js-style';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Helper function to convert payment mode names to Title Case
const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: '#fff',
    backgroundColor: '#333',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: '#444',
    },
    '&.Mui-focused': {
      backgroundColor: '#444',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#555',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#777',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#aaa',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
    // Add notch effect for the label
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#333', // Match the input's background
      padding: '0 4px',
    },
  },
  '& .MuiSvgIcon-root': { // Calendar icon
    color: '#fff',
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #9BEC00',
  borderRadius: '12px !important', // Ensure border radius is applied
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  '&:before': {
    display: 'none', // Remove default top border
  },
  '&.Mui-expanded': {
    margin: '16px 0',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root': {
    color: '#fff',
  },
});

const StyledTableCell = styled(TableCell)({
  color: '#fff',
  borderColor: '#444',
});

const StyledTableHeadCell = styled(StyledTableCell)({
  fontWeight: 'bold',
  backgroundColor: '#333',
  borderBottom: '2px solid #9BEC00',
});

function SalesHistory() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Date range state (from/to)
  // Set default to current ISO week (Monday to Sunday)
  const [fromDate, setFromDate] = useState(dayjs().startOf('isoWeek'));
  const [toDate, setToDate] = useState(dayjs().endOf('isoWeek'));
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const summaryTotals = useMemo(() => {
    const groupedByDate = filteredSales.reduce((acc, sale) => {
      const date = sale.date ? dayjs(sale.date).format('YYYY-MM-DD') : '-';
      if (!acc[date]) {
        acc[date] = {
          tickets: 0,
          total: 0,
          paymentBreakup: {},
        };
      }
      acc[date].tickets += sale.tickets || 0;
      const saleTotal = Object.values(sale.paymentBreakup || {}).reduce((sum, amt) => sum + Number(amt), 0);
      acc[date].total += saleTotal;

      if (sale.paymentBreakup) {
        Object.entries(sale.paymentBreakup).forEach(([mode, amt]) => {
          const normalizedMode = mode.toLowerCase().replace(/-/g, ' ');
          acc[date].paymentBreakup[normalizedMode] = (acc[date].paymentBreakup[normalizedMode] || 0) + Number(amt);
        });
      }
      return acc;
    }, {});

    return Object.values(groupedByDate).reduce((acc, data) => {
      acc.tickets += data.tickets;
      acc.grossSale += data.total;
      acc.netSale += data.total / 1.05;
      Object.entries(data.paymentBreakup).forEach(([mode, amt]) => {
        acc.paymentBreakup[mode] = (acc.paymentBreakup[mode] || 0) + amt;
      });
      return acc;
    }, {
      tickets: 0,
      grossSale: 0,
      netSale: 0,
      paymentBreakup: {},
    });
  }, [filteredSales]);

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line
  }, []);

  const fetchSales = async () => {
    // Fetch all POS summaries
    const dailySummariesCol = collection(db, 'posDailySummaries');
    const snapshot = await getDocs(dailySummariesCol);
    const data = snapshot.docs.map(doc => {
      const pos = { id: doc.id, ...doc.data() };
      // Use the 'date' field (should be YYYY-MM-DD)
      let date;
      if (pos.date && typeof pos.date === 'object' && pos.date.toDate) {
        date = dayjs(pos.date.toDate());
      } else if (pos.date) {
        // Parse Firestore YYYY-MM-DD string as local date (not UTC)
        date = dayjs(pos.date, 'YYYY-MM-DD');
      } else if (pos.createdAt && pos.createdAt.toDate) {
        date = dayjs(pos.createdAt.toDate());
      } else {
        date = null;
      }
      return {
        id: pos.id,
        date: date ? date.toISOString() : null,
        total: Number(pos.totalSales) || 0,
        cashSales: Number(pos.cashSales) || 0,
        paymentBreakup: pos.details || {},
        tickets: Number(pos.tickets) || 0 // fallback, if present
      };
    });
    setSales(data);
    filterSales(data, fromDate, toDate);
  };

  // Filtering logic using Settlement.js approach
  const filterSales = (salesArr, from, to) => {
    if (!from || !to) {
      setFilteredSales(salesArr);
      return;
    }
    const start = from.startOf('day');
    const end = to.endOf('day');

    setFilteredSales(
      salesArr.filter(sale => {
        if (!sale.date) return false;
        const saleDate = dayjs(sale.date);
        return saleDate.isSameOrAfter(start) && saleDate.isSameOrBefore(end);
      })
    );
  };

  // Filtering logic for from/to date (Settlement.js style)
  useEffect(() => {
    filterSales(sales, fromDate, toDate);
    // eslint-disable-next-line
  }, [fromDate, toDate, sales]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDownload = () => {
    const headerStyle = { 
      fill: { fgColor: { rgb: "C6E0B4" } }, // Light Green
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' }
    };

    const setColumnWidths = (worksheet, data) => {
      if (!data || data.length === 0) return;
      const objectMaxLength = [];
      const header = Object.keys(data[0]);
      data.forEach(row => {
        header.forEach((key, i) => {
          const len = (row[key] || "").toString().length;
          objectMaxLength[i] = Math.max(objectMaxLength[i] || 0, len);
        });
      });
      worksheet['!cols'] = header.map((h, i) => ({ 
        wch: Math.max(h.length, objectMaxLength[i]) + 2
      }));
    };

    const styleHeaders = (worksheet) => {
      if (!worksheet['!ref']) return;
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ c: C, r: range.s.r });
        if (worksheet[address]) {
          worksheet[address].s = headerStyle;
        }
      }
    };

    // Aggregate by date
    const byDate = filteredSales.reduce((acc, sale) => {
      const date = sale.date ? dayjs(sale.date).format('YYYY-MM-DD') : '-';
      if (!acc[date]) {
        acc[date] = {
          date,
          tickets: 0,
          total: 0,
          paymentBreakup: {},
        };
      }
      acc[date].tickets += sale.tickets || 0;
      if (sale.paymentBreakup) {
        acc[date].total += Object.values(sale.paymentBreakup).reduce((sum, amt) => sum + Number(amt), 0);
        Object.entries(sale.paymentBreakup).forEach(([mode, amt]) => {
          acc[date].paymentBreakup[mode] = (acc[date].paymentBreakup[mode] || 0) + Number(amt);
        });
      }
      return acc;
    }, {});

    // Collect all payment modes for columns
    const allModes = Array.from(new Set(
      Object.values(byDate).flatMap(row => Object.keys(row.paymentBreakup))
    ));

    const rows = Object.values(byDate).map(row => {
      const base = {
        'Date': dayjs(row.date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        'Tickets': row.tickets,
        'Gross Sale': row.total,
        'Net Sale': row.total / 1.05,
        'APC': row.tickets > 0 ? row.total / row.tickets : 0,
      };
      allModes.forEach(mode => {
        base[mode] = row.paymentBreakup[mode] || 0;
      });
      return base;
    }).sort((a, b) => dayjs(a.Date, 'DD-MM-YYYY').diff(dayjs(b.Date, 'DD-MM-YYYY')));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Apply styles and formatting
    styleHeaders(ws);
    setColumnWidths(ws, rows);

    // Format currency columns
    rows.forEach((row, index) => {
      const rowIndex = index + 2; // +1 for header, +1 for 1-based index
      ws[`C${rowIndex}`].z = '₹#,##0.00'; // Gross Sale
      ws[`D${rowIndex}`].z = '₹#,##0.00'; // Net Sale
      ws[`E${rowIndex}`].z = '₹#,##0.00'; // APC
      allModes.forEach((mode, i) => {
        const col = XLSX.utils.encode_col(5 + i);
        ws[`${col}${rowIndex}`].z = '₹#,##0.00';
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Summary');
    XLSX.writeFile(wb, 'SalesHistory.xlsx');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Banner
        title="Sales History"
        handleBack={() => navigate('/dashboard')}
        handleLogout={handleLogout}
      />
      {/* Main content wrapper with branded border and dark background */}
      <Container>        
        {/* Date Pickers and Action Buttons */}
        <Paper sx={{ p: 3, mb: 4, backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: 3, boxShadow: 4, marginTop: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <StyledDatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={v => setFromDate(v ? v.startOf('day') : null)}
                  maxDate={toDate}
                  format="DD-MM-YYYY"
                  slotProps={{ textField: { size: 'small' } }}
                />
                <StyledDatePicker
                  label="To Date"
                  value={toDate}
                  onChange={v => setToDate(v ? v.endOf('day') : null)}
                  minDate={fromDate}
                  format="DD-MM-YYYY"
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' }, gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    ['Today', 'Yesterday', 'This Week'],
                    ['Last Week', 'This Month', 'Last Month']
                  ].map((row, rowIndex) => (
                    <Box key={rowIndex} sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                      {row.map(label => (
                        <Button 
                          key={label} 
                          variant="outlined" 
                          size="small" 
                          onClick={() => {
                            switch(label) {
                              case 'Today':
                                setFromDate(dayjs().startOf('day'));
                                setToDate(dayjs().endOf('day'));
                                break;
                              case 'Yesterday':
                                setFromDate(dayjs().subtract(1, 'day').startOf('day'));
                                setToDate(dayjs().subtract(1, 'day').endOf('day'));
                                break;
                              case 'This Week':
                                setFromDate(dayjs().startOf('isoWeek'));
                                setToDate(dayjs().endOf('isoWeek'));
                                break;
                              case 'Last Week':
                                setFromDate(dayjs().subtract(1, 'week').startOf('isoWeek'));
                                setToDate(dayjs().subtract(1, 'week').endOf('isoWeek'));
                                break;
                              case 'This Month':
                                setFromDate(dayjs().startOf('month'));
                                setToDate(dayjs().endOf('month'));
                                break;
                              case 'Last Month':
                                setFromDate(dayjs().subtract(1, 'month').startOf('month'));
                                setToDate(dayjs().subtract(1, 'month').endOf('month'));
                                break;
                              default:
                                break;
                            }
                          }}
                          sx={{ flex: 1, color: '#affc41', borderColor: '#affc41', '&:hover': { borderColor: '#affc41', backgroundColor: 'rgba(175, 252, 65, 0.1)' } }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>

          {/* Summary and Download Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#9BEC00' }}>Summary</Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  <Typography><b>Tickets:</b> {summaryTotals.tickets}</Typography>
                  <Typography><b>Gross Sale:</b> ₹{(summaryTotals.grossSale || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</Typography>
                  <Typography><b>Net Sale:</b> ₹{(summaryTotals.netSale || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</Typography>
                  <Typography><b>APC:</b> ₹{((summaryTotals.grossSale / summaryTotals.tickets) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1 }}>
                  <Typography><b>Payments:</b></Typography>
                  {Object.entries(summaryTotals.paymentBreakup)
                    .sort(([, a], [, b]) => b - a)
                    .map(([mode, amount]) => (
                      <Typography key={mode} sx={{ backgroundColor: '#444', padding: '4px 8px', borderRadius: 1, whiteSpace: 'nowrap' }}>
                        {toTitleCase(mode)}: ₹{amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </Typography>
                    ))}
                </Box>
              </Box>
              <Button 
                variant="contained" 
                onClick={handleDownload}
                sx={{ backgroundColor: '#06D001', color: '#000', fontWeight: 'bold', '&:hover': { backgroundColor: '#9BEC00' }, width: { xs: '100%', md: 'auto' }, mt: { xs: 2, md: 0 } }}
              >
                Download Excel
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Sales Details Accordion */}
        <StyledAccordion defaultExpanded sx={{ border: '1px solid #444' }}>
          <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ color: '#9BEC00' }}>Sales Details</Typography>
          </StyledAccordionSummary>
          <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
            <TableContainer component={Paper} sx={{ backgroundColor: '#333', borderRadius: 2, border: '1px solid #444' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell>Date</StyledTableHeadCell>
                    <StyledTableHeadCell>Tickets</StyledTableHeadCell>
                    <StyledTableHeadCell>Gross Sale Amount</StyledTableHeadCell>
                    <StyledTableHeadCell>Net Amount</StyledTableHeadCell>
                    <StyledTableHeadCell>APC</StyledTableHeadCell>
                    <StyledTableHeadCell>Payment Mode Breakup</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const grouped = filteredSales.reduce((acc, sale) => {
                      const date = sale.date ? dayjs(sale.date).format('YYYY-MM-DD') : '-';
                      if (!acc[date]) {
                        acc[date] = {
                          date,
                          tickets: 0,
                          total: 0,
                          paymentBreakup: {},
                        };
                      }
                      acc[date].tickets += sale.tickets || 0;
                      if (sale.paymentBreakup) {
                        acc[date].total += Object.values(sale.paymentBreakup).reduce((sum, amt) => sum + Number(amt), 0);
                        Object.entries(sale.paymentBreakup).forEach(([mode, amt]) => {
                          acc[date].paymentBreakup[mode] = (acc[date].paymentBreakup[mode] || 0) + Number(amt);
                        });
                      }
                      return acc;
                    }, {});
                    const sorted = Object.entries(grouped).sort((a, b) => dayjs(a[0], 'YYYY-MM-DD').diff(dayjs(b[0], 'YYYY-MM-DD')));
                    return sorted.map(([date, data]) => (
                      <TableRow key={date} sx={{ '&:hover': { backgroundColor: '#444' } }}>
                        <StyledTableCell>{dayjs(date, 'YYYY-MM-DD').format('DD-MM-YYYY')}</StyledTableCell>
                        <StyledTableCell>{data.tickets}</StyledTableCell>
                        <StyledTableCell>₹{data.total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</StyledTableCell>
                        <StyledTableCell>₹{(data.total / 1.05).toLocaleString(undefined, { maximumFractionDigits: 2 })}</StyledTableCell>
                        <StyledTableCell>₹{(data.tickets > 0 ? data.total / data.tickets : 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {Object.entries(data.paymentBreakup).map(([mode, amt]) => (
                              <Typography key={mode} variant="body2">{toTitleCase(mode)}: ₹{Number(amt).toLocaleString(undefined, { maximumFractionDigits: 2 })}</Typography>
                            ))}
                          </Box>
                        </StyledTableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </StyledAccordion>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}> 
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SalesHistory;
